import MarkdownIt from 'markdown-it';
import markdownItTaskLists from 'markdown-it-task-lists';
import { buildFileUrl } from '@/config';

class MarkdownService {
  constructor() {
    this.md = null;
    this.availableFiles = [];
    this.initializeMarkdown();
  }

  initializeMarkdown() {
    this.md = new MarkdownIt({
      html: true,
      linkify: true,
      typographer: true,
      breaks: true
    });

    // Add task list support
    this.md.use(markdownItTaskLists, {
      enabled: true,
      label: true,
      labelAfter: true
    });

    this.setupCustomRenderers();
  }

  setupCustomRenderers() {
    // Custom renderer for images to handle @file: syntax
    const defaultImageRender = this.md.renderer.rules.image || function(tokens, idx, options, env, self) {
      return self.renderToken(tokens, idx, options);
    };

    this.md.renderer.rules.image = (tokens, idx, options, env, self) => {
      const token = tokens[idx];
      const srcIndex = token.attrIndex('src');
      
      if (srcIndex >= 0) {
        const src = token.attrs[srcIndex][1];
        
        // Handle custom @file: syntax for images
                  if (src.startsWith('@file:')) {
            const fileId = src.substring(6); // Remove '@file:' prefix
            const file = this.availableFiles.find(f => f.unique_id === fileId);
            
            if (file) {
              if (this.isImageFile(file)) {
                // Replace with actual file URL for images
                token.attrs[srcIndex][1] = buildFileUrl(file.unique_id, true);
                token.attrPush(['class', 'markdown-file-image']);
                token.attrPush(['data-filename', file.filename]);
                token.attrPush(['data-file-id', file.unique_id]);
              } else {
                // Not an image file, show error
                const errorSvg = this.createErrorSvg('Not an Image File', file.filename);
                token.attrs[srcIndex][1] = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(errorSvg);
                token.attrPush(['class', 'markdown-file-image-error']);
                token.attrPush(['data-filename', file.filename]);
                token.attrPush(['title', `Not an image file: ${file.filename}`]);
              }
            } else {
              // If no file list available (like in EntryView), try direct file URL
              if (this.availableFiles.length === 0) {
                // Directly build file URL without file list validation
                token.attrs[srcIndex][1] = buildFileUrl(fileId, true);
                token.attrPush(['class', 'markdown-file-image']);
                token.attrPush(['data-file-id', fileId]);
              } else {
                // File not found in available files, show placeholder or error
                const errorSvg = this.createErrorSvg('File Not Found', fileId);
                token.attrs[srcIndex][1] = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(errorSvg);
                token.attrPush(['class', 'markdown-file-image-error']);
                token.attrPush(['data-filename', fileId]);
                token.attrPush(['title', `File not found: ${fileId}`]);
              }
            }
        }
      }
      
      return defaultImageRender(tokens, idx, options, env, self);
    };

    // Custom renderer for links to handle @file: syntax
    const defaultLinkRender = this.md.renderer.rules.link_open || function(tokens, idx, options, env, self) {
      return self.renderToken(tokens, idx, options);
    };

    this.md.renderer.rules.link_open = (tokens, idx, options, env, self) => {
      const token = tokens[idx];
      const hrefIndex = token.attrIndex('href');
      
      if (hrefIndex >= 0) {
        const href = token.attrs[hrefIndex][1];
        
        // Handle custom @file: syntax for file downloads
                  if (href.startsWith('@file:')) {
            const fileId = href.substring(6); // Remove '@file:' prefix
            const file = this.availableFiles.find(f => f.unique_id === fileId);
            
            if (file) {
              token.attrs[hrefIndex][1] = buildFileUrl(file.unique_id, false);
              token.attrPush(['class', 'markdown-file-link']);
              token.attrPush(['data-filename', file.filename]);
              token.attrPush(['data-file-id', file.unique_id]);
              token.attrPush(['target', '_blank']);
              token.attrPush(['download', file.filename]);
            } else {
              // If no file list available (like in EntryView), try direct file URL
              if (this.availableFiles.length === 0) {
                // Directly build file URL without file list validation
                token.attrs[hrefIndex][1] = buildFileUrl(fileId, false);
                token.attrPush(['class', 'markdown-file-link']);
                token.attrPush(['data-file-id', fileId]);
                token.attrPush(['target', '_blank']);
                token.attrPush(['download', 'download-file']);
              } else {
                // File not found in available files, disable link
                token.attrs[hrefIndex][1] = '#';
                token.attrPush(['class', 'markdown-file-link-error']);
                token.attrPush(['data-filename', fileId]);
                token.attrPush(['title', `File not found: ${fileId}`]);
                token.attrPush(['onclick', 'return false;']);
              }
            }
        }
      }
      
      return defaultLinkRender(tokens, idx, options, env, self);
    };
  }

  // Helper method to create error SVG
  createErrorSvg(title, subtitle, bgColor = '#f8d7da', strokeColor = '#dc3545', textColor = '#6f4246') {
    return `<svg width="300" height="150" xmlns="http://www.w3.org/2000/svg">
      <rect width="100%" height="100%" fill="${bgColor}" stroke="${strokeColor}" stroke-width="2"/>
      <text x="50%" y="40%" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="14" fill="${textColor}">${title}</text>
      <text x="50%" y="60%" dominant-baseline="middle" text-anchor="middle" font-family="monospace" font-size="10" fill="${textColor}">${subtitle}</text>
    </svg>`;
  }

  // Helper method to check if file is an image
  isImageFile(file) {
    return file && file.mime_type && file.mime_type.startsWith('image/');
  }

  // Update available files for file references
  setAvailableFiles(files) {
    this.availableFiles = files || [];
  }

  // Render markdown content
  render(content) {
    if (!content || !this.md) {
      return '';
    }
    try {
      return this.md.render(content);
    } catch (error) {
      console.error('Error rendering markdown:', error);
      return content;
    }
  }

  // Check if content type is markdown
  isMarkdownContent(contentType) {
    return contentType === 'markdown';
  }

  // Format file size helper
  formatFileSize(bytes) {
    if (bytes === 0) return '0 B';
    
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  // Generate autocomplete suggestions for files
  generateFileSuggestions(availableFiles, partial = '') {
    const filteredFiles = availableFiles.filter(file => {
      return file.filename.toLowerCase().includes(partial.toLowerCase());
    });

    const suggestions = filteredFiles.map(file => {
      // Use unified @file: syntax with unique_id, but different markdown format based on file type
      let markdownText;
      if (this.isImageFile(file)) {
        // Images use image syntax with unique_id
        markdownText = `![${file.filename}](@file:${file.unique_id})`;
      } else {
        // Other files use link syntax with unique_id
        markdownText = `[${file.filename}](@file:${file.unique_id})`;
      }
      
      return {
        text: markdownText,
        displayText: `${file.filename} (${this.formatFileSize(file.file_size)})`,
        className: `autocomplete-file ${this.isImageFile(file) ? 'autocomplete-image' : 'autocomplete-file-link'}`,
        file: file
      };
    });

    // Sort by filename
    suggestions.sort((a, b) => a.file.filename.localeCompare(b.file.filename));
    return suggestions;
  }
}

// Export a singleton instance
export const markdownService = new MarkdownService();

// Export the class for creating new instances if needed
export { MarkdownService };
